import React, { useState, useRef, ChangeEvent } from 'react';
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardMedia,
  CardContent,
  SelectChangeEvent,
  Alert,
  Snackbar,
  IconButton,
  InputAdornment,
  Box
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LinkIcon from '@mui/icons-material/Link';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { motion, AnimatePresence } from 'framer-motion';
import { VideoMetadata, VideoQuality, videoService } from '../services/api';

// Extend HTMLInputElement to include directory selection attributes
declare module 'react' {
  interface InputHTMLAttributes<T> extends HTMLAttributes<T> {
    webkitdirectory?: string;
    directory?: string;
    mozdirectory?: string;
  }
}

interface VideoInfoProps {
  videoInfo: VideoMetadata;
  videoUrl: string;
  setError: (error: string | null) => void;
  onDelete?: () => void;
}

// Add mobile detection helper
const isMobileDevice = () => {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

const VideoInfo: React.FC<VideoInfoProps> = ({ videoInfo, videoUrl, setError: setGlobalError, onDelete }) => {
  const [selectedQuality, setSelectedQuality] = useState<VideoQuality>(
    videoInfo.availableQualities[0] || 'high'
  );
  const [selectedVideoIndex, setSelectedVideoIndex] = useState<number>(0);
  const [customFilename, setCustomFilename] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showSnackbar, setShowSnackbar] = useState<boolean>(false);

  // Determine if we're dealing with videos or images
  const hasVideos = videoInfo.videos.length > 0;
  const hasImages = (videoInfo.images?.length || 0) > 0;
  
  // Get the current media item based on the index
  const currentMedia = hasVideos 
    ? videoInfo.videos[selectedVideoIndex]
    : hasImages && videoInfo.images 
      ? videoInfo.images[selectedVideoIndex - (videoInfo.videos.length || 0)]
      : null;

  // Format duration only if it's a video and has duration
  const formattedDuration = hasVideos && currentMedia && 'duration' in currentMedia && typeof currentMedia.duration === 'number'
    ? `${Math.floor(currentMedia.duration / 60)}:${String(Math.floor(currentMedia.duration % 60)).padStart(2, '0')}`
    : null;

  // Get the appropriate thumbnail
  const mediaThumbnail = hasVideos 
    ? (currentMedia as VideoMetadata['videos'][number])?.thumbnail || videoInfo.thumbnail
    : hasImages && videoInfo.images 
      ? (currentMedia as NonNullable<VideoMetadata['images']>[number])?.url || videoInfo.thumbnail
      : videoInfo.thumbnail;

  // Get dimensions if available
  const dimensions = currentMedia?.width && currentMedia?.height
    ? `${currentMedia.width}x${currentMedia.height}`
    : null;

  const totalMediaCount = videoInfo.videos.length + (videoInfo.images?.length || 0);

  const handleQualityChange = (event: SelectChangeEvent) => {
    setSelectedQuality(event.target.value as VideoQuality);
  };

  const handleVideoChange = (event: SelectChangeEvent) => {
    setSelectedVideoIndex(Number(event.target.value));
  };

  const handleFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setCustomFilename(event.target.value);
  };

  const handleDownload = async () => {
    setLoading(true);
    setSuccess(null);
    setError(null);
    setGlobalError(null);
    setShowSnackbar(false);

    try {
      if (isMobileDevice()) {
        let mediaUrl = '';
        
        if (hasVideos) {
          const video = videoInfo.videos[selectedVideoIndex];
          // Get the variants and sort them by quality
          const variants = video.variants || [{ url: video.url }];
          const mp4Variants = variants
            .filter(v => v.url && v.url.includes('.mp4'))
            .sort((a, b) => {
              // If we have bitrates, use them
              if (a.bitrate && b.bitrate) {
                return (b.bitrate || 0) - (a.bitrate || 0);
              }
              // Otherwise try to extract quality from URL
              const getQuality = (url: string) => {
                const match = url.match(/\/(\d+)x(\d+)\//);
                return match ? parseInt(match[2]) : 0;
              };
              return getQuality(b.url || '') - getQuality(a.url || '');
            });

          // Select variant based on quality preference
          let selectedVariant;
          switch (selectedQuality) {
            case 'high':
              selectedVariant = mp4Variants[0]; // First is highest quality
              break;
            case 'medium':
              selectedVariant = mp4Variants[Math.floor(mp4Variants.length / 2)];
              break;
            case 'low':
              selectedVariant = mp4Variants[mp4Variants.length - 1];
              break;
            default:
              selectedVariant = mp4Variants[0];
          }

          mediaUrl = selectedVariant?.url || video.url;
          
          // Ensure URL is using HTTPS
          if (mediaUrl.startsWith('http:')) {
            mediaUrl = mediaUrl.replace('http:', 'https:');
          }
        } else if (hasImages && videoInfo.images) {
          mediaUrl = videoInfo.images[selectedVideoIndex - videoInfo.videos.length].url;
        }
            
        if (mediaUrl) {
          // For iOS Safari, create a simple HTML page with the video player
          const newWindow = window.open('', '_blank');
          if (newWindow) {
            const html = `
              <!DOCTYPE html>
              <html>
                <head>
                  <meta name="viewport" content="width=device-width, initial-scale=1">
                  <title>${hasVideos ? 'Video' : 'Image'} Player</title>
                  <style>
                    body { 
                      margin: 0; 
                      background: #000; 
                      height: 100vh; 
                      display: flex; 
                      align-items: center; 
                      justify-content: center; 
                    }
                    ${hasVideos ? `
                      video { 
                        width: 100%; 
                        height: auto;
                        max-height: 100vh;
                        background: #000;
                      }
                    ` : `
                      img { 
                        max-width: 100%; 
                        max-height: 100vh; 
                        object-fit: contain;
                      }
                    `}
                  </style>
                </head>
                <body>
                  ${hasVideos ? `
                    <video 
                      controls 
                      playsinline 
                      autoplay
                      preload="auto"
                      src="${mediaUrl}"
                    ></video>
                  ` : `
                    <img src="${mediaUrl}" alt="Image">
                  `}
                </body>
              </html>
            `;
            newWindow.document.write(html);
            newWindow.document.close();
          }
          setSuccess('Media opened in new tab');
        } else {
          throw new Error('Media URL not found');
        }
      } else {
        // Original PC download logic
        const result = await videoService.downloadVideo(
          videoUrl, 
          selectedQuality, 
          'downloads', 
          selectedVideoIndex,
          customFilename.trim()
        );
        if (result.success) {
          setSuccess(`Media download started: ${result.filePath}`);
        }
      }
      setShowSnackbar(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during download';
      setError(errorMessage);
      setGlobalError(errorMessage);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadAll = async () => {
    setLoading(true);
    setSuccess(null);
    setError(null);
    setGlobalError(null);
    setShowSnackbar(false);

    try {
      if (isMobileDevice()) {
        // For mobile devices, open all media URLs in new tabs
        const allMediaUrls = [
          ...videoInfo.videos.map(v => v.url),
          ...(videoInfo.images?.map(i => i.url) || [])
        ];

        allMediaUrls.forEach(url => {
          if (url) window.open(url, '_blank');
        });
        
        setSuccess(`Opened ${allMediaUrls.length} media items in new tabs`);
      } else {
        // Original PC download all logic
        const totalItems = videoInfo.videos.length + (videoInfo.images?.length || 0);
        let successCount = 0;

        // Download all videos first
        for (let i = 0; i < videoInfo.videos.length; i++) {
          try {
            const result = await videoService.downloadVideo(
              videoUrl,
              selectedQuality,
              'downloads',
              i,
              ''
            );
            if (result.success) successCount++;
          } catch (err) {
            console.error(`Error downloading video ${i + 1}:`, err);
          }
        }

        // Then download all images
        if (videoInfo.images) {
          for (let i = 0; i < videoInfo.images.length; i++) {
            try {
              const result = await videoService.downloadVideo(
                videoUrl,
                selectedQuality,
                'downloads',
                videoInfo.videos.length + i,
                ''
              );
              if (result.success) successCount++;
            } catch (err) {
              console.error(`Error downloading image ${i + 1}:`, err);
            }
          }
        }

        setSuccess(`Downloaded ${successCount} of ${totalItems} media files`);
      }
      setShowSnackbar(true);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred during download';
      setError(errorMessage);
      setGlobalError(errorMessage);
      setShowSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = () => {
    setShowSnackbar(false);
  };

  const handlePrevMedia = () => {
    setSelectedVideoIndex((prev) => (prev > 0 ? prev - 1 : totalMediaCount - 1));
  };

  const handleNextMedia = () => {
    setSelectedVideoIndex((prev) => (prev < totalMediaCount - 1 ? prev + 1 : 0));
  };

  const handleCopyLink = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(videoUrl);
    setSuccess('Link copied to clipboard');
    setShowSnackbar(true);
  };

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };

  return (
    <Paper elevation={3} className="p-4">
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ position: 'relative' }}>
            <Box sx={{ position: 'relative', paddingTop: '56.25%', backgroundColor: '#000000' }}>
              {totalMediaCount > 1 && (
                <>
                  <IconButton
                    onClick={handlePrevMedia}
                    sx={{
                      position: 'absolute',
                      left: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)'
                      },
                      zIndex: 2
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>
                  <IconButton
                    onClick={handleNextMedia}
                    sx={{
                      position: 'absolute',
                      right: 16,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.7)'
                      },
                      zIndex: 2
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </>
              )}
              <AnimatePresence initial={false} custom={selectedVideoIndex}>
                <motion.div
                  key={selectedVideoIndex}
                  custom={selectedVideoIndex}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{
                    x: { type: "spring", stiffness: 300, damping: 30 },
                    opacity: { duration: 0.2 }
                  }}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                >
                  <CardMedia
                    component="img"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'contain'
                    }}
                    image={mediaThumbnail}
                    alt={videoInfo.title}
                  />
                </motion.div>
              </AnimatePresence>
            </Box>
            <CardContent sx={{ 
              p: 2, 
              '&:last-child': { pb: 2 },
              height: 200
            }}>
              <Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  height: '100%'
                }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  {videoInfo.author && (
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 'bold',
                      mb: 0.5
                    }}>
                      {videoInfo.author.startsWith('@') ? (
                        <Box
                          component="span"
                          onClick={(e) => {
                            e.stopPropagation();
                            const username = videoInfo.author?.substring(1);
                            if (username) {
                              window.open(`https://x.com/${username}`, '_blank');
                            }
                          }}
                          sx={{
                            cursor: 'pointer',
                            color: 'inherit',
                            transition: 'color 0.2s ease',
                            '&:hover': {
                              color: 'primary.main'
                            }
                          }}
                        >
                          {videoInfo.author}
                        </Box>
                      ) : videoInfo.author}
                    </Typography>
                  )}
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      mb: 2,
                      display: '-webkit-box',
                      WebkitLineClamp: 4,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: 1.4
                    }}
                  >
                    {videoInfo.title}
                  </Typography>

                  {/* Spacer to push metadata to bottom */}
                  <Box sx={{ flex: 1 }} />

                  {/* Metadata section */}
                  <Box>
                    <Box sx={{ 
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                      mb: 1
                    }}>
                      {formattedDuration && (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          Duration: {formattedDuration}
                        </Typography>
                      )}
                      {dimensions && (
                        <Typography variant="body2" color="textSecondary" noWrap>
                          Resolution: {dimensions}
                        </Typography>
                      )}
                    </Box>
                    
                    {/* Bottom row with date and icons */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mt: 'auto'
                    }}>
                      <Typography variant="caption" color="textSecondary">
                        {new Date().toLocaleString(undefined, {
                          year: 'numeric',
                          month: 'numeric',
                          day: 'numeric',
                          hour: 'numeric',
                          minute: 'numeric',
                          hour12: true
                        })}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <IconButton
                          onClick={handleCopyLink}
                          sx={{
                            width: 20,
                            height: 20,
                            padding: 0,
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: 'primary.main'
                            }
                          }}
                        >
                          <LinkIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete?.();
                          }}
                          sx={{
                            width: 16,
                            height: 16,
                            padding: 0,
                            color: 'text.secondary',
                            '&:hover': {
                              backgroundColor: 'transparent',
                              color: 'error.main'
                            }
                          }}
                        >
                          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          onClick={() => window.open(videoUrl, '_blank')}
                          sx={{
                            width: 20,
                            height: 20,
                            padding: 0,
                            '&:hover': {
                              backgroundColor: 'transparent'
                            }
                          }}
                        >
                          <img
                            src="/xicon.webp"
                            alt="View on X"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain'
                            }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h6" gutterBottom>
              Download Options
            </Typography>
            
            {(videoInfo.videos.length + (videoInfo.images?.length || 0)) > 1 && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="media-select-label">Media</InputLabel>
                <Select
                  labelId="media-select-label"
                  value={selectedVideoIndex.toString()}
                  label="Media"
                  onChange={handleVideoChange}
                >
                  {videoInfo.videos.map((video, index) => (
                    <MenuItem key={`video-${index}`} value={index.toString()}>
                      Video {index + 1} {video.width && video.height ? `(${video.width}x${video.height})` : ''}
                    </MenuItem>
                  ))}
                  {videoInfo.images?.map((image, index) => (
                    <MenuItem key={`image-${index}`} value={(videoInfo.videos.length + index).toString()}>
                      Image {index + 1} {image.width && image.height ? `(${image.width}x${image.height})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {hasVideos && (
              <FormControl fullWidth margin="normal">
                <InputLabel id="quality-select-label">Video Quality</InputLabel>
                <Select
                  labelId="quality-select-label"
                  value={selectedQuality}
                  label="Video Quality"
                  onChange={handleQualityChange}
                >
                  {videoInfo.availableQualities.map((quality) => (
                    <MenuItem key={quality} value={quality}>
                      {quality.charAt(0).toUpperCase() + quality.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              fullWidth
              margin="normal"
              label="Custom Filename (optional)"
              variant="outlined"
              value={customFilename}
              onChange={handleFilenameChange}
              placeholder="Enter custom filename (without extension)"
              helperText="Leave empty to use auto-generated filename"
            />

            {/* Spacer to push buttons to bottom */}
            <Box sx={{ flexGrow: 1 }} />

            {/* Download Buttons */}
            <Box 
              sx={{ 
                display: 'flex', 
                gap: { xs: 1, sm: 2 }, // Smaller gap on mobile
                justifyContent: 'center', 
                mt: 2,
                flexWrap: 'nowrap' // Prevent wrapping
              }}
            >
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <CloudDownloadIcon />}
                onClick={handleDownload}
                disabled={loading}
                sx={{
                  whiteSpace: 'nowrap',
                  fontSize: { xs: '0.875rem', sm: '1rem' }, // Smaller font on mobile
                  padding: { xs: '6px 16px', sm: '8px 22px' } // Smaller padding on mobile
                }}
              >
                {loading ? 'Downloading...' : hasVideos ? 'Download Video' : 'Download Image'}
              </Button>

              {(videoInfo.videos.length + (videoInfo.images?.length || 0)) > 1 && (
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#2D3748',
                    '&:hover': {
                      backgroundColor: '#4A5568'
                    },
                    whiteSpace: 'nowrap',
                    fontSize: { xs: '0.875rem', sm: '1rem' }, // Smaller font on mobile
                    padding: { xs: '6px 16px', sm: '8px 22px' } // Smaller padding on mobile
                  }}
                  size="large"
                  startIcon={loading ? <CircularProgress size={24} color="inherit" /> : <CloudDownloadIcon />}
                  onClick={handleDownloadAll}
                  disabled={loading}
                >
                  {loading ? 'Downloading...' : 'Download All'}
                </Button>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>

      <Snackbar
        open={showSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={success ? "success" : "error"}
          variant="filled"
        >
          {success || error}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default VideoInfo; 